import { getDynamicCashFlowTopIds, getRowDisplayName } from '@/services/commons.service'
import { useCtxStore } from '@/stores/ctx.store'
import { createPlanningViewStore } from '@/stores/planningview.store'
import { computed } from 'vue'
import type { PlanningRowData } from '~/composables/unified-edit-api.ts'
import { StcsFpnaTypeEnum, StcsFstmtEnum } from '~/protos/gen/stcs_enums.entity'
import { useNuxtApp } from '#imports'
// Tree structure for hierarchical menus
interface TreeNode<T> {
  label: string
  data?: T
  children: TreeNode<T>[]
}

// Menu item interface for formula context menu
interface FormulaMenuNode {
  key: string
  label: string
  type: 'category' | 'statement' | 'row'
  data?: PlanningRowData
  statementKey?: StcsFstmtEnum
  children?: FormulaMenuNode[]
}

const allowedFpnaTypes = [StcsFpnaTypeEnum.SFT_TOTAL_CML, StcsFpnaTypeEnum.SFT_TOTAL_NCML, StcsFpnaTypeEnum.SFT_SUB_TOTAL, StcsFpnaTypeEnum.SFT_LEVEL_1, StcsFpnaTypeEnum.SFT_LEVEL_2]

/**
 * Composable for managing formula-related financial data across all formula components.
 * Centralizes the logic for merging actuals and planning data while applying consistent filtering.
 */
export function useFormulaData() {
  const ctxStore = useCtxStore()
  const planningStore = createPlanningViewStore(ctxStore.active_comp_id)

  if (!planningStore.selectedPlanState) {
    throw new Error('No selected plan state found in planning store')
  }

  /**
   * Merged financial statement data with consistent filtering applied
   */
  const financialStatementData = computed(() => {
    const refData = ctxStore.ref_comp_map[ctxStore.active_comp_id]?.ref_comp_actuals_res_fstmt

    if (!planningStore.selectedPlanState) {
      throw new Error('No selected plan state found in planning store')
    }

    return {
      pnl_list: mergeAndFilterRows(refData?.pnl_list || [], planningStore.selectedPlanState.pnlList || []),
      bs_list: mergeAndFilterRows(refData?.bs_list || [], planningStore.selectedPlanState.bsList || []),
      cf_list: filterFormulaRowsOrStructureRows(refData?.cf_list || []),
    }
  })

  /**
   * All available financial rows for formula references (excludes ctx rows and custom planning rows)
   */
  const availableFormulaRows = computed(() => {
    const data = financialStatementData.value

    const allRows = [...data.pnl_list, ...data.bs_list, ...data.cf_list]

    return filterFormulaRowsOrStructureRows(allRows)
  })

  /**
   * Gets rows for a specific financial statement
   */
  function getRowsDataByFstmt(statementKey: StcsFstmtEnum): PlanningRowData[] {
    const data = financialStatementData.value

    switch (statementKey) {
      case StcsFstmtEnum.SFSE_PNL:
        return data.pnl_list
      case StcsFstmtEnum.SFSE_BS:
        return data.bs_list
      case StcsFstmtEnum.SFSE_CF:
        return data.cf_list
      default:
        return []
    }
  }

  /**
   * Gets child rows for a specific parent row.
   * Works with dynamic path_level arrays that adapt based on active level filters.
   * A child row is one whose path_level starts with the parent's path_level and is exactly one level deeper.
   */
  function getChildRowsForParent(parentRow: PlanningRowData, statementKey: StcsFstmtEnum): PlanningRowData[] {
    const allRows = getRowsDataByFstmt(statementKey)
    const parentPath = parentRow.path_level || []
    const parentPathLength = parentPath.length

    // A direct child has a path_level that:
    // 1. Is exactly one level deeper than parent (length = parentPathLength + 1)
    // 2. Starts with the exact same path as the parent
    return filterFormulaRowsOrStructureRows(allRows).filter((row) => {
      const rowPath = row.path_level
      if (!rowPath || rowPath.length !== parentPathLength + 1) return false

      // Check if the row's path starts with the parent's path
      for (let i = 0; i < parentPathLength; i++) {
        if (rowPath[i] !== parentPath[i]) return false
      }
      return true
    })
  }

  /**
   * Checks if a row can be used as a formula reference
   */
  function canUseAsFormulaReference(row: PlanningRowData): boolean {
    if (isCtxRow(row)) return false
    if (isCustomPlanningRow(row)) return false
    if (!allowedFpnaTypes.includes(row.fpna_type)) return false
    return true
  }

  /**
   * Gets prioritized cash flow data (similar to buildPercentOfSubMenu logic)
   */
  function getPrioritizedCfData(): PlanningRowData[] {
    const rawCf = ctxStore.ref_comp_map[ctxStore.active_comp_id]?.ref_comp_actuals_res_fstmt?.cf_list || []

    // Apply the same filtering as other financial statements to exclude SFT_LEVEL_3 and other unwanted types
    const cfClean = filterFormulaRowsOrStructureRows(rawCf)

    // Priority items that should appear at the top
    const topIds = getDynamicCashFlowTopIds(rawCf)

    const topItems = topIds.map((id) => cfClean.find((item) => item.row_id === id)).filter(Boolean) as PlanningRowData[]

    const otherItems = cfClean.filter((item) => !topIds.includes(item.row_id))

    return [...topItems, ...otherItems]
  }

  /**
   * Gets hierarchical data for formula context menu (replicates buildPercentOfSubMenu structure)
   * Includes both fsdOrg data and planning positions for complete hierarchy
   */
  function getHierarchicalFormulaData() {
    // Get merged financial statement data that includes planning positions
    const mergedData = financialStatementData.value

    // For P&L and BS, use merged data that includes planning positions
    // For CF, use prioritized CF data from actuals only
    return {
      pnl_list: mergedData.pnl_list,
      bs_list: mergedData.bs_list,
      cf_list: getPrioritizedCfData(),
    }
  }

  /**
   * Rebuilds full unfiltered path_level for a row by walking up the parent chain.
   * Level filters modify path_level by removing filtered-out entries, but the context menu
   * needs the complete hierarchy including all levels (Level 1, Level 2, Level 3, etc.)
   */
  function rebuildFullPathLevel(row: PlanningRowData, allRows: PlanningRowData[]): string[] {
    // If path_level is already complete or this is a top-level row, return as-is
    if (!row.path_level || row.path_level.length <= 1) {
      return row.path_level || []
    }

    // Get the parent row ID (second-to-last in current path_level)
    const parentRowId = row.path_level[row.path_level.length - 2]

    // Find the parent row in all available rows
    const parentRow = allRows.find((r) => r.row_id === parentRowId)

    if (!parentRow) {
      // Parent not found, return current path_level as-is
      return row.path_level
    }

    // Recursively rebuild parent's path_level first
    const parentFullPath = rebuildFullPathLevel(parentRow, allRows)

    // Build this row's full path by appending row_id to parent's full path
    return [...parentFullPath, row.row_id]
  }

  /**
   * Builds a hierarchical tree structure from flat financial data (replicates buildPathTree logic).
   * Reconstructs full unfiltered path_level for all rows before building the tree to ensure
   * proper hierarchy when level filters are active.
   */
  function buildFormulaPathTree(rows: PlanningRowData[]): TreeNode<PlanningRowData>[] {
    const root: Record<string, TreeNode<PlanningRowData> & { _map?: Record<string, TreeNode<PlanningRowData>> }> = {}

    // Rebuild full path_level for all rows before building tree
    const rowsWithFullPaths = rows.map((r) => ({
      ...r,
      path_level: rebuildFullPathLevel(r, rows),
    }))

    for (const r of rowsWithFullPaths) {
      let here = root

      r.path_level.forEach((segment, idx) => {
        if (!here[segment]) {
          here[segment] = { label: segment, children: [] }
        }
        if (idx < r.path_level.length - 1) {
          if (!here[segment]._map) {
            here[segment]._map = {}
            here[segment].children = []
          }
          here = here[segment]._map || {}
        } else {
          here[segment].data = r
        }
      })
    }

    function toArray(map: Record<string, TreeNode<PlanningRowData> & { _map?: Record<string, TreeNode<PlanningRowData>> }>): TreeNode<PlanningRowData>[] {
      return Object.values(map).map((n) => ({
        label: n.label,
        data: n.data,
        children: toArray(n._map ?? {}),
      }))
    }

    const result = toArray(root)
    return result
  }

  /**
   * Converts tree structure to formula menu nodes (replicates flattenToMenu logic)
   */
  function treeToFormulaMenu(nodes: TreeNode<PlanningRowData>[], statementKey: StcsFstmtEnum): FormulaMenuNode[] {
    return nodes
      .filter((n) => {
        const rt = n.data?.fpna_type
        if (!rt) return false
        return allowedFpnaTypes.includes(rt)
      })
      .map((n) => {
        const hasKids = n.children.length > 0
        const label = n.data ? getRowDisplayName(n.data).name : n.label
        const key = n.data?.row_id || n.label

        const node: FormulaMenuNode = {
          key,
          label,
          type: 'row' as const,
          data: n.data,
          statementKey,
        }

        if (hasKids) {
          node.children = treeToFormulaMenu(n.children, statementKey)
        }

        return node
      })
  }

  /**
   * Gets hierarchical menu structure for formula context menu (replicates buildPercentOfSubMenu)
   */
  function getHierarchicalFormulaMenu() {
    const hierarchicalData = getHierarchicalFormulaData()
    const $tolgee = useNuxtApp().$tolgee

    const createStatementMenu = (label: string, items: PlanningRowData[], statementKey: StcsFstmtEnum): FormulaMenuNode => {
      const tree = buildFormulaPathTree(items)
      const menu = treeToFormulaMenu(tree, statementKey)

      return {
        key: statementKey.toString(),
        label,
        type: 'statement',
        statementKey,
        children: menu,
      }
    }

    return [
      createStatementMenu($tolgee.t('general-terms-pnl'), hierarchicalData.pnl_list, StcsFstmtEnum.SFSE_PNL),
      createStatementMenu($tolgee.t('general-terms-bs'), hierarchicalData.bs_list, StcsFstmtEnum.SFSE_BS),
      createStatementMenu($tolgee.t('general-terms-cf'), hierarchicalData.cf_list, StcsFstmtEnum.SFSE_CF),
    ]
  }

  /**
   * Gets context menu rows for a specific statement (replicates getContextMenuRows logic)
   */
  function getContextMenuRows(statementKey: StcsFstmtEnum): PlanningRowData[] {
    const rows = getRowsDataByFstmt(statementKey)

    if (statementKey === StcsFstmtEnum.SFSE_CF) {
      return rows.filter((row) => row.fpna_type === StcsFpnaTypeEnum.SFT_LEVEL_1 && !isCustomPlanningRow(row))
    }

    return rows.filter((row) => (row.fpna_type === StcsFpnaTypeEnum.SFT_TOTAL_CML || row.fpna_type === StcsFpnaTypeEnum.SFT_TOTAL_NCML || row.fpna_type === StcsFpnaTypeEnum.SFT_LEVEL_2) && !isCtxRow(row) && !isCustomPlanningRow(row))
  }

  // Helper functions
  function mergeAndFilterRows(actualsRows: PlanningRowData[], planningRows: PlanningRowData[]): PlanningRowData[] {
    // Deduplicate by row_id, preferring planning rows (which carry is_struct_item from plan config)
    const rowMap = new Map<string, PlanningRowData>()
    for (const row of actualsRows) {
      rowMap.set(row.row_id, row)
    }
    for (const row of planningRows) {
      rowMap.set(row.row_id, row)
    }
    return filterFormulaRowsOrStructureRows([...rowMap.values()])
  }

  function filterFormulaRowsOrStructureRows(rows: PlanningRowData[]): PlanningRowData[] {
    return rows.filter((row) => {
      // Hide structural position children but keep structural Level 2 parent rows visible
      if (row.is_struct_item && row.is_position_row) return false
      if (isCustomPlanningRow(row)) return false
      if (!allowedFpnaTypes.includes(row.fpna_type)) return false
      return true
    })
  }

  function isCtxRow(row: PlanningRowData): boolean {
    return row.row_id.includes('_ctx_line')
  }

  function isCustomPlanningRow(row: PlanningRowData): boolean {
    return row.rowClass === 'custom-planning-row' || row.rowClass === 'selected-method-row'
  }

  function getCombinedLevelId(row: PlanningRowData): string {
    // 1. if not level 1 row, return level_id as is
    if (row.fpna_type !== StcsFpnaTypeEnum.SFT_LEVEL_1) {
      return row.level_id || ''
    }

    // 2. get level_id and row_id from the row
    const levelId = row.level_id || ''
    const rowId = row.row_id || ''

    // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
    if (!levelId || !rowId) {
      return levelId
    }

    // 3. make new id by removing level_id from the end of row_id
    // row_id ends with `_${level_id}`, so remove it
    const suffix = `_${levelId}`
    if (!rowId.endsWith(suffix)) {
      return levelId
    }
    const parentRowId = rowId.slice(0, rowId.length - suffix.length)

    // 4. find the row in data with this parentRowId that has fpna_type = SFT_LEVEL_1
    const allRows = getRowsDataByFstmt(row.fstmt_type)
    const parentRow = allRows.find((r) => r.row_id === parentRowId)

    if (!parentRow) {
      return levelId
    }

    // 5. get level_id of that parent row
    const parentLevelId = parentRow.level_id || ''

    // 6. return parent level_id + underscore + current level_id
    return parentLevelId ? `${parentLevelId}_${levelId}` : levelId
  }

  return {
    // Computed properties
    financialStatementData,
    availableFormulaRows,

    // Methods
    getChildRowsForParent,
    getContextMenuRows,
    getRowsDataByFstmt,
    canUseAsFormulaReference,
    getPrioritizedCfData,
    getHierarchicalFormulaData,
    getHierarchicalFormulaMenu,
    buildFormulaPathTree,
    treeToFormulaMenu,

    // Helper functions
    isCtxRow,
    isCustomPlanningRow,
    getCombinedLevelId,
  }
}
